import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faInfoCircle,
  faClock,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";

const ProcessingTimeChart = ({
  data,
  calculateExitTime,
  calculatePaymentTime,
}) => {
  const [processingData, setProcessingData] = useState({
    exitTime: {
      average: 0,
      min: 0,
      max: 0,
      dataPoints: 0,
      distribution: [],
    },
    paymentTime: {
      average: 0,
      min: 0,
      max: 0,
      dataPoints: 0,
      distribution: [],
    },
  });

  useEffect(() => {
    if (!data || data.length === 0) {
      setProcessingData({
        exitTime: {
          average: 0,
          min: 0,
          max: 0,
          dataPoints: 0,
          distribution: [],
        },
        paymentTime: {
          average: 0,
          min: 0,
          max: 0,
          dataPoints: 0,
          distribution: [],
        },
      });
      return;
    }

    // Process exit time data
    const exitTimes = data
      .map((item) => calculateExitTime(item))
      .filter((time) => time !== null);

    // Process payment time data
    const paymentTimes = data
      .map((item) => calculatePaymentTime(item))
      .filter((time) => time !== null);

    // Calculate exit time statistics
    const exitTimeAvg = exitTimes.length
      ? Math.round(
          exitTimes.reduce((sum, val) => sum + val, 0) / exitTimes.length
        )
      : 0;

    const exitTimeMin = exitTimes.length ? Math.min(...exitTimes) : 0;
    const exitTimeMax = exitTimes.length ? Math.max(...exitTimes) : 0;

    // Calculate payment time statistics
    const paymentTimeAvg = paymentTimes.length
      ? Math.round(
          paymentTimes.reduce((sum, val) => sum + val, 0) / paymentTimes.length
        )
      : 0;

    const paymentTimeMin = paymentTimes.length ? Math.min(...paymentTimes) : 0;
    const paymentTimeMax = paymentTimes.length ? Math.max(...paymentTimes) : 0;

    // Create distribution buckets
    const exitTimeDistribution = createDistribution(exitTimes);
    const paymentTimeDistribution = createDistribution(paymentTimes);

    setProcessingData({
      exitTime: {
        average: exitTimeAvg,
        min: exitTimeMin,
        max: exitTimeMax,
        dataPoints: exitTimes.length,
        distribution: exitTimeDistribution,
      },
      paymentTime: {
        average: paymentTimeAvg,
        min: paymentTimeMin,
        max: paymentTimeMax,
        dataPoints: paymentTimes.length,
        distribution: paymentTimeDistribution,
      },
    });
  }, [data, calculateExitTime, calculatePaymentTime]);

  // Helper function to create distribution buckets
  const createDistribution = (values) => {
    if (!values.length) return [];

    // Create buckets in ranges of 5 days
    const buckets = {};
    values.forEach((val) => {
      // Round to nearest 5
      const bucket = Math.floor(val / 5) * 5;
      buckets[bucket] = (buckets[bucket] || 0) + 1;
    });

    // Convert to array and sort
    return Object.entries(buckets)
      .map(([days, count]) => ({
        days: parseInt(days, 10),
        count,
        label: `${days}-${parseInt(days, 10) + 4} days`,
      }))
      .sort((a, b) => a.days - b.days);
  };

  if (
    processingData.exitTime.dataPoints === 0 &&
    processingData.paymentTime.dataPoints === 0
  ) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-500">
        <FontAwesomeIcon icon={faChartLine} className="text-4xl mb-2" />
        <p>No processing time data available</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-800 flex items-center">
          <FontAwesomeIcon
            icon={faChartLine}
            className="mr-2 text-purple-600"
          />
          Processing Times
        </h3>
        <div className="text-xs text-gray-500 flex items-center">
          <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
          From ETA date
        </div>
      </div>

      <div className="flex-grow overflow-auto">
        {/* Exit Time Section */}
        <div className="mb-6">
          <div className="flex items-center mb-2">
            <FontAwesomeIcon icon={faClock} className="mr-2 text-blue-500" />
            <h4 className="text-sm font-medium text-gray-700">
              Tiempo de Salida (ETA to Crossing)
            </h4>
          </div>

          {processingData.exitTime.dataPoints > 0 ? (
            <>
              <div className="flex space-x-4 mb-3">
                <div className="bg-blue-50 rounded-lg p-2 flex-1">
                  <div className="text-xs text-gray-500">Average</div>
                  <div className="text-lg font-semibold text-blue-700">
                    {processingData.exitTime.average} days
                  </div>
                </div>
                <div className="bg-blue-50 rounded-lg p-2 flex-1">
                  <div className="text-xs text-gray-500">Range</div>
                  <div className="text-sm font-medium text-blue-700">
                    {processingData.exitTime.min} -{" "}
                    {processingData.exitTime.max} days
                  </div>
                </div>
                <div className="bg-blue-50 rounded-lg p-2 flex-1">
                  <div className="text-xs text-gray-500">References</div>
                  <div className="text-lg font-semibold text-blue-700">
                    {processingData.exitTime.dataPoints}
                  </div>
                </div>
              </div>

              {/* Distribution Chart */}
              <div className="space-y-2">
                {processingData.exitTime.distribution.map((bucket) => (
                  <div key={bucket.days} className="group">
                    <div className="flex justify-between items-center mb-1">
                      <div className="text-xs font-medium text-gray-700">
                        {bucket.label}
                      </div>
                      <div className="text-xs text-gray-500">
                        {bucket.count}
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-full rounded-full"
                        style={{
                          width: `${
                            (bucket.count /
                              Math.max(
                                ...processingData.exitTime.distribution.map(
                                  (d) => d.count
                                )
                              )) *
                            100
                          }%`,
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-sm text-gray-500 py-2">
              No exit time data available
            </div>
          )}
        </div>

        {/* Payment Time Section */}
        <div>
          <div className="flex items-center mb-2">
            <FontAwesomeIcon
              icon={faCalendarAlt}
              className="mr-2 text-green-500"
            />
            <h4 className="text-sm font-medium text-gray-700">
              Arrival to Pedimento Payment
            </h4>
          </div>

          {processingData.paymentTime.dataPoints > 0 ? (
            <>
              <div className="flex space-x-4 mb-3">
                <div className="bg-green-50 rounded-lg p-2 flex-1">
                  <div className="text-xs text-gray-500">Average</div>
                  <div className="text-lg font-semibold text-green-700">
                    {processingData.paymentTime.average} days
                  </div>
                </div>
                <div className="bg-green-50 rounded-lg p-2 flex-1">
                  <div className="text-xs text-gray-500">Range</div>
                  <div className="text-sm font-medium text-green-700">
                    {processingData.paymentTime.min} -{" "}
                    {processingData.paymentTime.max} days
                  </div>
                </div>
                <div className="bg-green-50 rounded-lg p-2 flex-1">
                  <div className="text-xs text-gray-500">References</div>
                  <div className="text-lg font-semibold text-green-700">
                    {processingData.paymentTime.dataPoints}
                  </div>
                </div>
              </div>

              {/* Distribution Chart */}
              <div className="space-y-2">
                {processingData.paymentTime.distribution.map((bucket) => (
                  <div key={bucket.days} className="group">
                    <div className="flex justify-between items-center mb-1">
                      <div className="text-xs font-medium text-gray-700">
                        {bucket.label}
                      </div>
                      <div className="text-xs text-gray-500">
                        {bucket.count}
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-500 h-full rounded-full"
                        style={{
                          width: `${
                            (bucket.count /
                              Math.max(
                                ...processingData.paymentTime.distribution.map(
                                  (d) => d.count
                                )
                              )) *
                            100
                          }%`,
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-sm text-gray-500 py-2">
              No payment time data available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProcessingTimeChart;
